import { useFormikContext } from "formik";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

// Predefined mapping of spoken keywords to Formik field names
const keywordsMap = {
  "company name": "companyname",
  "company": "companyname",
  "first name": "firstname",
  "last name": "lastname",
  "billing address": "billingAddress.address1",
  "shipping address": "shippingAddress.address1",
  "billing city": "billingAddress.city",
  "shipping city": "shippingAddress.city",
  "billing pin code": "billingAddress.pincode",
  "shipping pin code": "shippingAddress.pincode",
  "billing state": "billingAddress.state",
  "shipping state": "shippingAddress.state",
  "billing country": "billingAddress.country",
  "shipping country": "shippingAddress.country",
  "gst number": "gstnumber",
  "gst in": "gstnumber",
  "gst": "gstnumber",
  "address 1": "address1",
  "address 2": "address2",
  "address 3": "address3",
  "address one": "address1",
  "address to": "address2",
  "address three": "address3",
  "assigned to": "assignedto",
  "assigneed to": "assignedto",
  "assigne to": "assignedto",
  "assigning to": "assignedto",
  "assign to": "assignedto",
  "assigned user": "assignedto",
  "assign user": "assignedto",
  "assigned member": "assignedto",
  "assign member": "assignedto",
  "assigned person": "assignedto",
  "assign person": "assignedto",
  "assigned": "assignedto",
  "assignee": "assignedto",
  "assigneed": "assignedto",
  "assigning": "assignedto",
  "assign": "assignedto",
  "due date": "duedate",
  "duedate": "duedate",
  "due dates": "duedate",
  "deadline": "duedate",
  "due": "duedate",
  "date": "duedate",
  "subject": "subject",
  "suject": "subject",
  "subect": "subject",
  "title": "subject",
  "task name": "subject",
  "task subject": "subject",
  "task title": "subject",
  "priority": "priority",
  "prioriti": "priority",
  "prio": "priority",
  "importance": "priority",
  "status": "status",
  "stutus": "status",
  "state": "status",
  "description": "notes",
  "details": "notes",
  "remarks": "notes",
  "remark": "notes",
  "task notes": "notes",
  "task note": "notes",
  "notes": "notes",
  "note": "notes",
  "noted": "notes",
  "mobile": "mobile",
  "phone": "altphone",
  "email": "email",
  "department": "department",
  "designation": "designation",
  "category": "category",
  "report to": "reportTo",
  "website": "website",
  "city": "city",
  "pin code": "pincode",
  "pincode": "pincode",
  "country": "country",
};

const selectOptionsMap = {
  priority: ["High", "Medium", "Low", "Critical", "Normal", "Emergency", "Deferred"],
  status: [
    "Open", "InProgress", "Pending", "Completed", "Canceled", "Deferred",
    "WaitingforCustomer", "WaitingforInternalReview", "Reassigned", "OnHold",
    "Overdue", "Scheduled", "NeedsMoreInformation", "Escalated", "Closed", "NotApplicable"
  ],
  assignedto: ["John Red", "Option 1", "Option 2", "Option 3"]
};

const priorityAliases = {
  low: "Low",
  law: "Low",
  lo: "Low",
  loow: "Low",
  lwo: "Low",
  minimum: "Low",
  high: "High",
  hi: "High",
  height: "High",
  hie: "High",
  hai: "High",
  maximum: "High",
  top: "High",
  medium: "Medium",
  med: "Medium",
  mid: "Medium",
  midium: "Medium",
  average: "Medium",
  critical: "Critical",
  kritical: "Critical",
  critial: "Critical",
  crit: "Critical",
  normal: "Normal",
  normel: "Normal",
  regular: "Normal",
  emergency: "Emergency",
  urgency: "Emergency",
  urgent: "Emergency",
  deferred: "Deferred",
  deffered: "Deferred",
  delay: "Deferred",
};

const statusAliases = {
  open: "Open",
  opened: "Open",
  opn: "Open",
  active: "Open",
  "in progress": "InProgress",
  inprogress: "InProgress",
  progress: "InProgress",
  working: "InProgress",
  ongoing: "InProgress",
  pending: "Pending",
  panding: "Pending",
  waiting: "Pending",
  completed: "Completed",
  complete: "Completed",
  done: "Completed",
  finished: "Completed",
  resolved: "Completed",
  canceled: "Canceled",
  cancelled: "Canceled",
  cancel: "Canceled",
  cancled: "Canceled",
  deferred: "Deferred",
  deffered: "Deferred",
  "waiting for customer": "WaitingforCustomer",
  "waiting customer": "WaitingforCustomer",
  "waiting for internal review": "WaitingforInternalReview",
  "internal review": "WaitingforInternalReview",
  reassigned: "Reassigned",
  "on hold": "OnHold",
  onhold: "OnHold",
  hold: "OnHold",
  overdue: "Overdue",
  late: "Overdue",
  scheduled: "Scheduled",
  schedule: "Scheduled",
  "needs more information": "NeedsMoreInformation",
  escalated: "Escalated",
  closed: "Closed",
  close: "Closed",
  "not applicable": "NotApplicable",
};

const parseDateToYYYYMMDD = (val) => {
  if (!val) return "";
  let cleaned = val.trim().toLowerCase();

  if (cleaned === "today") {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (cleaned === "tomorrow") {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  cleaned = cleaned.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
  cleaned = cleaned.replace(/\bof\b/gi, "").replace(/\s+/g, " ").trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    return cleaned;
  }

  const parts = cleaned.split(/[-/.]/);
  if (parts.length === 3 && parts.every(p => /^\d+$/.test(p.trim()))) {
    let [p1, p2, p3] = parts.map(p => p.trim());
    if (p1.length === 4) {
      const mm = p2.padStart(2, '0');
      const dd = p3.padStart(2, '0');
      return `${p1}-${mm}-${dd}`;
    } else if (p3.length === 4) {
      const yyyy = p3;
      let num1 = parseInt(p1, 10);
      let num2 = parseInt(p2, 10);
      let mm, dd;
      if (num1 > 12 && num2 <= 12) {
        dd = String(num1).padStart(2, '0');
        mm = String(num2).padStart(2, '0');
      } else if (num2 > 12 && num1 <= 12) {
        mm = String(num1).padStart(2, '0');
        dd = String(num2).padStart(2, '0');
      } else {
        dd = String(num1).padStart(2, '0');
        mm = String(num2).padStart(2, '0');
      }
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  const d = new Date(cleaned);
  if (!isNaN(d.getTime())) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return val;
};

const matchSelectOption = (key, val) => {
  if (!val) return val;
  const cleanVal = val.toLowerCase().replace(/[^a-z0-9]/g, "").trim();

  if (key === "priority") {
    if (priorityAliases[cleanVal]) return priorityAliases[cleanVal];
    for (const [alias, target] of Object.entries(priorityAliases)) {
      if (cleanVal.includes(alias)) return target;
    }
  }

  if (key === "status") {
    if (statusAliases[cleanVal]) return statusAliases[cleanVal];
    const fullLower = val.toLowerCase().trim();
    if (statusAliases[fullLower]) return statusAliases[fullLower];
    for (const [alias, target] of Object.entries(statusAliases)) {
      const cleanAlias = alias.replace(/[^a-z0-9]/g, "");
      if (cleanVal.includes(cleanAlias)) return target;
    }
  }

  const options = selectOptionsMap[key];
  if (!options) return val;
  const found = options.find(
    (opt) => opt.toLowerCase().replace(/[^a-z0-9]/g, "") === cleanVal
  );
  return found || val;
};

const cleanNoisePhrases = (val) => {
  if (!val) return "";
  return val
    .replace(/^(should be|must be|will be|can be|set to|is set to|to be|to|is|=|:)\s+/gi, "")
    .trim();
};

const extractFieldsFromTranscript = (transcript) => {
  const lower = transcript.toLowerCase();
  const sortedKeys = Object.keys(keywordsMap).sort((a, b) => b.length - a.length);
  const occupied = new Array(lower.length).fill(false);
  const matches = [];

  for (const key of sortedKeys) {
    let pos = 0;
    while ((pos = lower.indexOf(key, pos)) !== -1) {
      const endPos = pos + key.length;
      const isStartWord = pos === 0 || /[\s,.:;!?-]/.test(lower[pos - 1]);
      const isEndWord = endPos === lower.length || /[\s,.:;!?-]/.test(lower[endPos]);

      if (isStartWord && isEndWord) {
        let overlap = false;
        for (let i = pos; i < endPos; i++) {
          if (occupied[i]) {
            overlap = true;
            break;
          }
        }
        if (!overlap) {
          for (let i = pos; i < endPos; i++) occupied[i] = true;
          matches.push({
            start: pos,
            end: endPos,
            key: key,
            fieldKey: keywordsMap[key]
          });
        }
      }
      pos = endPos;
    }
  }

  matches.sort((a, b) => a.start - b.start);

  const results = {};
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const valStart = current.end;
    const valEnd = (i + 1 < matches.length) ? matches[i + 1].start : lower.length;

    let rawVal = transcript.substring(valStart, valEnd).trim();
    rawVal = cleanNoisePhrases(rawVal);

    if (rawVal) {
      results[current.fieldKey] = rawVal;
    }
  }

  return results;
};

const SpeechToText = ({ inputId, style = {} }) => {
  const [recognizing, setRecognizing] = useState(false);
  const [hasVoiceInput, setHasVoiceInput] = useState(false);
  const [populatedFields, setPopulatedFields] = useState([]);

  const { setFieldValue } = useFormikContext();
  const isExplicitlyStopped = useRef(false);
  const recognitionRef = useRef(null);

  // FIX: Added missing pendingDropdownRef
  const pendingDropdownRef = useRef(null);

  const capitalizeFirstLetter = (string) =>
    string ? string.charAt(0).toUpperCase() + string.slice(1) : "";

  const setInputValue = (targetId, value) => {
    const inputElement = document.getElementById(targetId);
    if (inputElement) {
      if (inputElement.type !== "file") {
        inputElement.value = value;
      }
    }
  };

  const ensureSelectOption = (fieldKey, value) => {
    if (!value) return;
    const selectElem =
      document.getElementById(fieldKey) ||
      document.getElementsByName(fieldKey)[0];
    if (selectElem && selectElem.options) {
      const exists = Array.from(selectElem.options).some(
        (opt) => opt.value.toLowerCase() === value.toLowerCase()
      );
      if (!exists) {
        const newOpt = document.createElement("option");
        newOpt.value = value;
        newOpt.innerText = value;
        selectElem.appendChild(newOpt);
      }
    }
  };

  const handleClearVoiceInput = () => {
    setInputValue(inputId, "");

    if (populatedFields.length > 0) {
      populatedFields.forEach((fieldKey) => {
        if (fieldKey === "priority") {
          setFieldValue(fieldKey, "High");
        } else if (fieldKey === "status") {
          setFieldValue(fieldKey, "Open");
        } else {
          setFieldValue(fieldKey, "");
        }
      });
    } else {
      setFieldValue("subject", "");
      setFieldValue("duedate", "");
      setFieldValue("notes", "");
      setFieldValue("assignedto", "");
      setFieldValue("priority", "High");
      setFieldValue("status", "Open");
      setFieldValue("firstname", "");
      setFieldValue("lastname", "");
      setFieldValue("mobile", "");
      setFieldValue("altphone", "");
      setFieldValue("email", "");
    }

    setHasVoiceInput(false);
    setPopulatedFields([]);
  };

  const handleName = (transcript) => {
    const cleanedTranscript = transcript
      .toLowerCase()
      .replace(/name|full\sname/g, "")
      .trim();
    const words = cleanedTranscript.split(" ");

    if (words.length < 2) {
      setFieldValue("firstname", capitalizeFirstLetter(words[0]));
      setFieldValue("lastname", "");
    } else {
      setFieldValue("firstname", capitalizeFirstLetter(words[0]));
      setFieldValue(
        "lastname",
        capitalizeFirstLetter(words.slice(1).join(" "))
      );
    }
    setHasVoiceInput(true);
    setPopulatedFields((prev) => Array.from(new Set([...prev, "firstname", "lastname"])));
  };

  const handleMobileOrPhone = (transcript) => {
    const cleanedTranscript = transcript.toLowerCase().trim();
    if (cleanedTranscript.startsWith("mobile")) {
      setFieldValue("mobile", cleanedTranscript.replace("mobile", "").trim());
      setPopulatedFields((prev) => Array.from(new Set([...prev, "mobile"])));
    } else if (cleanedTranscript.startsWith("phone")) {
      setFieldValue("altphone", cleanedTranscript.replace("phone", "").trim());
      setPopulatedFields((prev) => Array.from(new Set([...prev, "altphone"])));
    }
    setHasVoiceInput(true);
  };

  const handleEmail = (transcript) => {
    let email = transcript
      .replace(/email|gmail/g, "")
      .trim()
      .toLowerCase();
    email = email.replace(/ /g, "");
    if (!email.includes("@")) {
      email += "@gmail.com";
    }
    setFieldValue("email", email);
    setHasVoiceInput(true);
    setPopulatedFields((prev) => Array.from(new Set([...prev, "email"])));
  };

  const startRecognition = () => {
    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }

    try {
      const recognitionInstance = new SpeechRecognitionAPI();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = navigator.language || "en-US";

      recognitionInstance.onstart = () => setRecognizing(true);

      recognitionInstance.onerror = (event) => {
        console.error("Recognition error:", event.error);
        if (event.error === "not-allowed") {
          toast.error("Microphone access denied. Please allow microphone access.");
        } else if (event.error === "no-speech") {
          toast.warning("No speech detected. Please try again.");
        }
        stopRecognition();
      };

      recognitionInstance.onend = () => {
        setRecognizing(false);
        // Don't auto-restart - let user control manually
      };

      recognitionInstance.onresult = (event) => {
        try {
          const latestResult = event.results[event.results.length - 1];
          if (latestResult.isFinal) {
            const transcript = latestResult[0].transcript.trim();
            const lowercaseText = transcript.toLowerCase();

            setInputValue(inputId, transcript);
            setHasVoiceInput(true);

            if (/^(name|full name)/i.test(transcript)) {
              handleName(transcript);
              return;
            }

            if (pendingDropdownRef.current) {
              const { selectName, sortedOptions } = pendingDropdownRef.current;
              let matchedOption = null;
              for (let opt of sortedOptions) {
                const optText = opt.text.toLowerCase();
                const optValue = opt.value.toLowerCase();
                if (lowercaseText.includes(optText) || lowercaseText.includes(optValue)) {
                  matchedOption = opt.value;
                  break;
                }
              }
              if (matchedOption) {
                setFieldValue(selectName, matchedOption);
                setInputValue(inputId, `${selectName} = ${matchedOption}`);
                pendingDropdownRef.current = null;
                return;
              }
            }

            // 3. Try to match a dropdown trigger + option in this result
            const selects = document.querySelectorAll("select");
            let dropdownMatched = false;

            for (let select of selects) {
              const selectName = select.name || select.id;
              if (!selectName) continue;

              const lowerName = selectName.toLowerCase();
              let matchesSelect = false;
              if (lowerName.includes("status") && lowercaseText.includes("status")) {
                matchesSelect = true;
              } else if ((lowerName.includes("source") || lowerName.includes("leadsource")) &&
                (lowercaseText.includes("source") || lowercaseText.includes("lead source"))) {
                matchesSelect = true;
              } else if ((lowerName.includes("contact") || lowerName.includes("bestwaycontact")) &&
                (lowercaseText.includes("best way to contact") || lowercaseText.includes("best way") || lowercaseText.includes("way to contact"))) {
                matchesSelect = true;
              }

              if (matchesSelect) {
                const options = Array.from(select.options);
                let matchedOption = null;

                const sortedOptions = [...options].sort((a, b) => b.text.length - a.text.length);

                for (let opt of sortedOptions) {
                  const optText = opt.text.toLowerCase();
                  const optValue = opt.value.toLowerCase();

                  if (lowercaseText.includes(optText) || lowercaseText.includes(optValue)) {
                    matchedOption = opt.value;
                    break;
                  }
                }

                if (matchedOption) {
                  if (setFieldValue) {
                    setFieldValue(selectName, matchedOption);
                    setInputValue(inputId, `${selectName.replace("leadsource", "Source").replace("bestwaycontact", "Best Way to Contact")} = ${matchedOption}`);
                  }
                } else {
                  pendingDropdownRef.current = { selectName, sortedOptions };
                  setInputValue(inputId, `Listening for ${selectName.replace("leadsource", "Source").replace("bestwaycontact", "Best Way to Contact").replace("status", "Status")} value...`);
                  return;
                }
              }
            }

            const extractedFields = extractFieldsFromTranscript(transcript);
            const keys = Object.keys(extractedFields);

            if (keys.length > 0) {
              setPopulatedFields((prev) => Array.from(new Set([...prev, ...keys])));
            }

            Object.entries(extractedFields).forEach(([fieldKey, rawValue]) => {
              if (fieldKey !== "fileUpload") {
                let finalValue = rawValue;
                if (fieldKey === "duedate") {
                  finalValue = parseDateToYYYYMMDD(rawValue);
                } else if (selectOptionsMap[fieldKey]) {
                  finalValue = matchSelectOption(fieldKey, rawValue);
                  ensureSelectOption(fieldKey, finalValue);
                } else {
                  finalValue = capitalizeFirstLetter(rawValue);
                }
                setFieldValue(fieldKey, finalValue);
              }
            });
          }
        } catch (err) {
          console.error("Speech result handling error:", err);
        }
      };

      recognitionInstance.start();
      recognitionRef.current = recognitionInstance;
    } catch (err) {
      console.error("Failed to start voice recognition:", err);
      toast.error("Couldn't start voice input. You can still fill the form manually.");
      setRecognizing(false);
    }
  };

  const stopRecognition = () => {
    isExplicitlyStopped.current = true;
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch (err) {
      console.error("Failed to stop voice recognition:", err);
    }
    setRecognizing(false);
    setInputValue(inputId, "");
  };

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      isExplicitlyStopped.current = true;
      try {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      } catch (e) {
        // Ignore errors on cleanup
      }
    };
  }, []);

  const buttonStyle = {
    backgroundColor: recognizing ? "#dc3545" : "#6f42c1",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: recognizing
      ? "0 0 15px rgba(220, 53, 69, 0.8)"
      : "0 2px 5px rgba(0, 0, 0, 0.2)",
    animation: recognizing ? "pulse 1.5s infinite" : "none",
    ...style,
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      <button
        className="btn btn-primary"
        type="button"
        onClick={recognizing ? stopRecognition : startRecognition}
        title={recognizing ? "Stop listening" : "Start voice input"}
        style={buttonStyle}
      >
        <i className={`fas ${recognizing ? "fa-stop" : "fa-microphone"}`}></i>
      </button>

      {hasVoiceInput && (
        <button
          className="btn btn-outline-danger"
          type="button"
          onClick={handleClearVoiceInput}
          title="Clear voice input and fields"
          style={{
            borderColor: "#dc3545",
            color: "#dc3545",
            background: "transparent",
            borderRadius: "4px",
            padding: "6px 10px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#dc3545";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#dc3545";
          }}
        >
          <i className="fas fa-times"></i>
        </button>
      )}
    </div>
  );
};

export default SpeechToText;